import { useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useStoreState } from "easy-peasy";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { hasRoles, hasActiveSubscription } from "../utils/globalVariables";
import { useCurrentUser } from "../utils/useCurrentUser";
import { getAuthKey } from "../utils/apiUtils";
import { clearLocalStorage } from "../utils/functions";
import {
  BadgeDollarSignIcon,
  Ban,
  Bookmark,
  BoxIcon,
  Building2Icon,
  Calendar,
  CircleQuestionMarkIcon,
  Clock,
  MessageSquareHeart,
  Newspaper,
  Receipt,
  ShieldUser,
  ShirtIcon,
  Sparkles,
  Users,
  UserStarIcon,
} from "lucide-react";

const tileClass =
  "group flex flex-col items-center justify-center gap-3 p-6 md:p-8 rounded-2xl text-center bg-white border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-khamco-ribbon/30 transition-all min-h-[10rem] md:min-h-[12rem]";

const iconWrapClass =
  "flex items-center justify-center w-14 h-14 rounded-full bg-khamco-ribbon/10 text-khamco-ribbon group-hover:bg-khamco-ribbon group-hover:text-white transition-colors";

const labelClass = "text-gray-700 font-semibold text-base";

const Home = () => {
  const authKey = getAuthKey();
  const navigate = useNavigate();
  const user = useStoreState((state) => state.user);
  const { fullProfile } = useCurrentUser(true);
  const { t } = useTranslation();

  const isTokenExpired = useCallback((token) => {
    const payloadBase64 = token.split(".")[1];
    const decodedJson = atob(payloadBase64);
    const decoded = JSON.parse(decodedJson);
    const exp = decoded.exp;
    const now = Date.now().valueOf() / 1000;
    return now > exp;
  }, []);

  useEffect(() => {
    document.title = "Khayat Go Home";

    const isLoggedIn = () => {
      const token = authKey;
      return token != null && !isTokenExpired(token);
    };

    if (!isLoggedIn()) {
      clearLocalStorage();
      navigate("/users/login");
    }

    if (!user) {
      toast.warn(t("You are not authorized to access that resource."));
    }
  }, [isTokenExpired, authKey, navigate, user, t]);

  return (
    <>
      <Navbar />
      <main className="p-[1rem] md:p-[4rem] w-[92%] max-w-7xl m-auto">
        <div className="text-center mb-10">
          <h1 className="text-[2rem] font-minimoBold text-khamco-secondary">
            {t("Main Menu")}
          </h1>
          <p className="text-gray-500 mt-2 font-minimoMedium">
            {t("Everything you need to run your shop, in one place.")}
          </p>
        </div>

        <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          <Link to="/about" className={tileClass}>
            <span className={iconWrapClass}>
              <CircleQuestionMarkIcon size={24} />
            </span>
            <p className={labelClass}>{t("About")}</p>
          </Link>
          {hasRoles(
            [
              "SuperAdministrator",
              "Administrator",
              "Measurer",
              "Driver",
              "Developer",
            ],
            user
          ) && (
            <Link to="/customers" className={tileClass}>
              <span className={iconWrapClass}>
                <Users size={24} />
              </span>
              <p className={labelClass}>{t("Customers")}</p>
            </Link>
          )}
          {hasRoles(
            [
              "SuperAdministrator",
              "Administrator",
              "Measurer",
              "Cutter",
              "Driver",
              "Developer",
            ],
            user
          ) && (
            <Link to="/products" className={tileClass}>
              <span className={iconWrapClass}>
                <BoxIcon size={24} />
              </span>
              <p className={labelClass}>{t("Inventory")}</p>
            </Link>
          )}

          {/* Tailor Dashboard: Always show */}
          <Link to={`/users/my-dashboard/${user?.id}`} className={tileClass}>
            <span className={iconWrapClass}>
              <UserStarIcon size={24} />
            </span>
            <p className={labelClass}>{t("My Dashboard")}</p>
          </Link>
          {hasRoles(
            [
              "SuperAdministrator",
              "Administrator",
              "Measurer",
              "Driver",
              "Developer",
            ],
            user
          ) && (
            <Link to="/services" className={tileClass}>
              <span className={iconWrapClass}>
                <ShirtIcon size={24} />
              </span>
              <p className={labelClass}>{t("Services")}</p>
            </Link>
          )}
          <Link to="/pricing" className={tileClass}>
              <span className={iconWrapClass}>
                <BadgeDollarSignIcon size={24} />
              </span>
              <p className={labelClass}>{t("Pricing")}</p>
            </Link>
          {hasRoles(
            ["SuperAdministrator", "Administrator", "Measurer", "Developer"],
            user
          ) && (
            <Link to="/invoices" className={tileClass}>
              <span className={iconWrapClass}>
                <Receipt size={24} />
              </span>
              <p className={labelClass}>{t("Invoices")}</p>
            </Link>
          )}
          {hasRoles(["SuperAdministrator", "Administrator", "Developer"], user) && (
            <Link to="/orders" className={tileClass}>
              <span className={iconWrapClass}>
                <Bookmark size={24} />
              </span>
              <p className={labelClass}>{t("Orders")}</p>
            </Link>
          )}
          {hasRoles(
            [
              "SuperAdministrator",
              "Administrator",
              "Measurer",
              "Driver",
              "Developer",
            ],
            user
          ) && (
            <Link to="/appointments" className={tileClass}>
              <span className={iconWrapClass}>
                <Calendar size={24} />
              </span>
              <p className={labelClass}>{t("Appointments")}</p>
            </Link>
          )}
          {hasRoles(
            ["SuperAdministrator", "Administrator", "Measurer", "Developer"],
            user
          ) && (
            <Link to="/waiting-time" className={tileClass}>
              <span className={iconWrapClass}>
                <Clock size={24} />
              </span>
              <p className={labelClass}>{t("Time")}</p>
            </Link>
          )}
          {hasRoles(["SuperAdministrator", "Administrator", "Developer"], user) && (
            <Link to="/branches" className={tileClass}>
              <span className={iconWrapClass}>
                <Building2Icon size={24} />
              </span>
              <p className={labelClass}>{t("Manage Branches")}</p>
            </Link>
          )}
          {hasRoles(["SuperAdministrator", "Developer"], user) && (
            <Link to="/admin/manage-admins" className={tileClass}>
              <span className={iconWrapClass}>
                <ShieldUser size={24} />
              </span>
              <p className={labelClass}>{t("Manage Admins")}</p>
            </Link>
          )}
          {hasRoles(["SuperAdministrator", "Administrator", "Developer"], user) && (
            <Link to="/admin/dashboard" className={tileClass}>
              <span className={iconWrapClass}>
                <Ban size={24} />
              </span>
              <p className={labelClass}>{t("Administration")}</p>
            </Link>
          )}
          <Link to="/feedback" className={tileClass}>
            <span className={iconWrapClass}>
              <MessageSquareHeart size={24} />
            </span>
            <p className={labelClass}>{t("Feedback & Bug Reports")}</p>
          </Link>
          {hasRoles(["SuperAdministrator", "Developer"], user) && (
            <Link to="/admin/bug-reports" className={tileClass}>
              <span className={iconWrapClass}>
                <MessageSquareHeart size={24} />
              </span>
              <p className={labelClass}>{t("Bug Reports Dashboard")}</p>
            </Link>
          )}
          {hasRoles(["SuperAdministrator", "Developer"], user) && (
            <Link to="/admin/articles" className={tileClass}>
              <span className={iconWrapClass}>
                <Newspaper size={24} />
              </span>
              <p className={labelClass}>{t("Manage Articles")}</p>
            </Link>
          )}
        </div>

        {hasRoles(["Administrator"], user) &&
          !hasActiveSubscription(fullProfile) && (
            <Link
              to="/pricing"
              className="flex flex-col md:flex-row items-center justify-between gap-4 mt-10 p-6 md:p-8 rounded-2xl bg-gradient-to-r from-khamco-ribbon to-khamco-primary text-white text-center md:text-left shadow-lg hover:opacity-95 transition-all"
            >
              <div className="flex items-center gap-4">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-white/15 shrink-0">
                  <Sparkles size={28} />
                </span>
                <div>
                  <p className="text-xl font-minimoBold">
                    {t("Unlock more features for your shop")}
                  </p>
                  <p className="text-sm opacity-90 mt-1">
                    {t("Upgrade your plan or start a 120-day free trial today.")}
                  </p>
                </div>
              </div>
              <span className="bg-white text-khamco-primary px-6 py-2.5 rounded-full font-minimoBold whitespace-nowrap shadow-sm">
                {t("Upgrade Plan")}
              </span>
            </Link>
          )}
      </main>
      <Footer />
    </>
  );
};

export default Home;
